import { z } from 'zod';

// Pagination Schema
export const PaginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  total: z.number().int().min(0).optional(),
  totalPages: z.number().int().min(0).optional()
});

export type Pagination = z.infer<typeof PaginationSchema>;

// Date Range Schema
export const DateRangeSchema = z.object({
  start: z.date(),
  end: z.date()
}).refine((data) => data.end >= data.start, {
  message: 'End date must be greater than or equal to start date',
  path: ['end']
});

export type DateRange = z.infer<typeof DateRangeSchema>;

// Search Query Schema
export const SearchQuerySchema = z.object({
  query: z.string().min(1, 'Search query is required'),
  limit: z.number().int().min(1).max(100).default(20)
});

export type SearchQuery = z.infer<typeof SearchQuerySchema>;

// Sort Schema
export const SortSchema = z.object({
  field: z.string().min(1, 'Sort field is required'),
  order: z.enum(['asc', 'desc']).default('asc')
});

export type Sort = z.infer<typeof SortSchema>;

// Filter Schema
export const FilterSchema = z.object({
  field: z.string().min(1, 'Filter field is required'),
  operator: z.enum(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'regex']),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
});

export type Filter = z.infer<typeof FilterSchema>;

// API Response Schema
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) => z.object({
  success: z.boolean(),
  data: dataSchema.optional(),
  message: z.string().optional(),
  error: z.string().optional(),
  pagination: PaginationSchema.optional()
});

export type ApiResponse<T> = z.infer<ReturnType<typeof ApiResponseSchema<z.ZodType<T>>>>;

// Error Response Schema
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string().min(1, 'Error message is required'),
  code: z.string().optional(),
  details: z.record(z.any()).optional(),
  timestamp: z.date().optional()
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// Success Response Schema
export const SuccessResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) => z.object({
  success: z.literal(true),
  data: dataSchema,
  message: z.string().optional(),
  timestamp: z.date().optional()
});

export type SuccessResponse<T> = z.infer<ReturnType<typeof SuccessResponseSchema<z.ZodType<T>>>>;

// Validation Error Schema
export const ValidationErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
  value: z.any().optional()
});

export type ValidationError = z.infer<typeof ValidationErrorSchema>;

// Bulk Operation Schema
export const BulkOperationSchema = z.object({
  operation: z.enum(['create', 'update', 'delete']),
  ids: z.array(z.string()).min(1, 'At least one ID is required'),
  data: z.record(z.any()).optional()
});

export type BulkOperation = z.infer<typeof BulkOperationSchema>;

// Bulk Operation Result Schema
export const BulkOperationResultSchema = z.object({
  success: z.boolean(),
  total: z.number().int().min(0),
  successful: z.number().int().min(0),
  failed: z.number().int().min(0),
  errors: z.array(ValidationErrorSchema).optional()
});

export type BulkOperationResult = z.infer<typeof BulkOperationResultSchema>;

// File Upload Schema
export const FileUploadSchema = z.object({
  filename: z.string().min(1, 'Filename is required'),
  mimetype: z.string().min(1, 'MIME type is required'),
  size: z.number().int().positive('File size must be positive'),
  buffer: z.any().optional(), // Buffer only exists in Node; avoid browser bundle crash
  path: z.string().optional()
});

export type FileUpload = z.infer<typeof FileUploadSchema>;

// Device Info Schema
export const DeviceInfoSchema = z.object({
  deviceId: z.string().min(1, 'Device ID is required'),
  platform: z.string().optional(),
  version: z.string().optional(),
  userAgent: z.string().optional(),
  ipAddress: z.string().optional()
});

export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;

// Audit Log Schema
export const AuditLogSchema = z.object({
  actorId: z.string(),
  actorRole: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  before: z.record(z.any()).optional(),
  after: z.record(z.any()).optional(),
  ip: z.string().optional(),
  deviceId: z.string().optional(),
  timestamp: z.date().optional(),
  metadata: z.record(z.any()).optional()
});

export type AuditLog = z.infer<typeof AuditLogSchema>; 